import { useEffect, useRef, useState, useCallback } from 'react';

const WS_RECONNECT_INTERVAL = 3000;
const WS_PING_INTERVAL = 30000;

export const useWebSocket = (token, onNotification, onBidUpdate, onNewMessage, onCartUpdate) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  const connect = useCallback(() => {
    if (!token) return;

    // Get WebSocket URL from backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);

        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, WS_PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'notification':
              if (onNotification) {
                onNotification(data.data);
              }
              break;
            case 'bid_update':
              if (onBidUpdate) {
                onBidUpdate(data.product_id, data.data);
              }
              break;
            case 'new_message':
              if (onNewMessage) {
                onNewMessage(data.data);
              }
              break;
            case 'cart_update':
              if (onCartUpdate) {
                onCartUpdate(data);
              }
              break;
            case 'pong':
              // Keepalive response, ignore
              break;
            case 'connected':
              console.log('WebSocket confirmed connected');
              break;
            default:
              console.log('Unknown WebSocket message type:', data.type);
          }
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Attempt to reconnect if not intentional close
        if (event.code !== 1000 && event.code !== 4001 && event.code !== 4002 && event.code !== 4003) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting WebSocket reconnect...');
            connect();
          }, WS_RECONNECT_INTERVAL);
        }
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
    }
  }, [token, onNotification, onBidUpdate, onNewMessage, onCartUpdate]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User logout');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const subscribeToProduct = useCallback((productId) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe_product',
        product_id: productId
      }));
    }
  }, []);

  useEffect(() => {
    if (token) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [token, connect, disconnect]);

  return {
    isConnected,
    subscribeToProduct,
    disconnect
  };
};

export default useWebSocket;
